import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Loader2, ShieldCheck, Sparkles, Timer, Users } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import {
  getWaitlistEntry,
  getWaitlistPosition,
  togglePaymentIntent,
} from "@/platform/waitlist/api";

const uspAccent = {
  "--color-accent": "var(--color-accent)",
  "--color-accent-deep": "var(--color-accent-deep)",
  "--color-accent-soft": "var(--color-accent-soft)",
} as React.CSSProperties;

const PRE_PAYMENT_BRL = 50;

export function UspWaitlistPage() {
  const queryClient = useQueryClient();

  const entryQuery = useQuery({
    queryKey: ["waitlist", "usp"],
    queryFn: () => getWaitlistEntry("usp"),
  });

  const positionQuery = useQuery({
    queryKey: ["waitlist", "usp", "position"],
    queryFn: () => getWaitlistPosition("usp"),
    enabled: Boolean(entryQuery.data),
  });

  const intentMutation = useMutation({
    mutationFn: ({ intent }: { intent: boolean }) =>
      togglePaymentIntent("usp", intent, intent ? PRE_PAYMENT_BRL : undefined),
    onSuccess: (data) => {
      queryClient.setQueryData(["waitlist", "usp"], data);
      toast.success(
        data.signal_payment_intent
          ? "Intenção registrada. Te avisamos quando o checkout abrir."
          : "Intenção removida.",
      );
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Não foi possível atualizar.");
    },
  });

  if (entryQuery.isLoading) {
    return <LoadingScreen />;
  }

  if (!entryQuery.data) {
    return <NotOnWaitlistScreen />;
  }

  const entry = entryQuery.data;
  const position = positionQuery.data?.position;
  const total = positionQuery.data?.total ?? 0;
  const hasIntent = entry.signal_payment_intent;

  return (
    <div style={uspAccent}>
      {/* Hero confirmação */}
      <section className="relative overflow-hidden bg-mesh">
        <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob" />
        <SoftStar color="mint" className="pointer-events-none absolute right-10 bottom-20 w-24 opacity-60 animate-float" />

        <Container size="default" className="relative pt-16 pb-20 md:pt-24">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] transition-colors hover:text-[color:var(--color-foreground)]"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Voltar para a plataforma
          </Link>

          <div className="mt-8 animate-fade-up">
            <Badge
              className="gap-1.5"
              style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
            >
              <CheckCircle2 className="size-3" aria-hidden />
              Você está na lista
            </Badge>

            <h1 className="mt-7 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
              <span className="text-gradient-accent">FuvestPro USP.</span> Cadastro confirmado.
            </h1>

            <p className="mt-5 max-w-2xl text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              Quando atingirmos demanda mínima validada e parecer jurídico favorável,
              abriremos o beta com vagas limitadas. Lista de espera tem prioridade e
              desconto vitalício.
            </p>

            {position && total > 0 && (
              <div className="mt-10 inline-flex flex-col items-start gap-1 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] px-6 py-5 shadow-[var(--shadow-sm)]">
                <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                  Sua posição
                </span>
                <div className="flex items-baseline gap-2">
                  <span
                    data-tabular="true"
                    className="font-display text-[length:var(--text-display-md)] font-semibold leading-none"
                    style={{ color: "var(--color-accent-deep)" }}
                  >
                    #{position}
                  </span>
                  <span className="text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                    de {total}
                  </span>
                </div>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Pré-pagamento sinal (Gate 1) */}
      <section className="border-t border-[--color-border] py-20 md:py-24">
        <Container size="default">
          <div className="max-w-3xl">
            <Badge variant="outline" className="w-fit gap-1.5">
              <Sparkles className="size-3" aria-hidden />
              Garanta vaga no beta
            </Badge>
            <h2 className="mt-5 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Sinalize intenção de pré-pagamento
            </h2>
            <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-relaxed text-pretty">
              Nenhuma cobrança agora. Apenas sinalizamos para você quando o checkout
              abrir, com prioridade na ordem de cadastro e desconto vitalício de R$ {PRE_PAYMENT_BRL}
              {" "}sobre o plano escolhido.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <article
              className={`rounded-2xl border-2 p-7 transition-colors duration-200 ${
                hasIntent
                  ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]"
                  : "border-[--color-border] bg-[color:var(--color-card)]"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className="chip size-10"
                  style={{ backgroundColor: "var(--color-accent-soft)", color: "var(--color-accent-deep)" }}
                >
                  <CheckCircle2 className="size-5" aria-hidden />
                </span>
                {hasIntent && (
                  <Badge
                    className="gap-1"
                    style={{ backgroundColor: "var(--color-accent)", color: "white", border: "none" }}
                  >
                    <CheckCircle2 className="size-3" aria-hidden />
                    Sinalizado
                  </Badge>
                )}
              </div>
              <h3 className="mt-5 font-display text-[length:var(--text-body-lg)] font-semibold">
                Tenho interesse no pré-pagamento de R$ {PRE_PAYMENT_BRL}
              </h3>
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                Não cobra agora. Quando o beta abrir, você recebe um link de checkout
                exclusivo com o desconto aplicado.
              </p>
              <Button
                size="lg"
                variant={hasIntent ? "outline" : "primary"}
                className="mt-6 w-full"
                loading={intentMutation.isPending}
                onClick={() => intentMutation.mutate({ intent: !hasIntent })}
              >
                {hasIntent ? "Remover intenção" : "Sinalizar intenção"}
                {!hasIntent && <ArrowUpRight className="size-4" aria-hidden />}
              </Button>
            </article>

            <article className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-7">
              <span
                className="chip size-10"
                style={{ backgroundColor: "var(--color-accent-soft)", color: "var(--color-accent-deep)" }}
              >
                <Users className="size-5" aria-hidden />
              </span>
              <h3 className="mt-5 font-display text-[length:var(--text-body-lg)] font-semibold">
                Como decidimos abrir o beta
              </h3>
              <ul className="mt-3 space-y-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] leading-relaxed">
                <li className="flex gap-2">
                  <Timer className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>8+ pré-cadastros pagos validam a demanda inicial.</span>
                </li>
                <li className="flex gap-2">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>Parecer jurídico favorável sobre uso de provas Fuvest.</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>POC do pipeline com custo por aluno aceitável.</span>
                </li>
              </ul>
            </article>
          </div>
        </Container>
      </section>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="size-8 animate-spin text-[color:var(--color-muted-foreground)]" aria-hidden />
    </div>
  );
}

function NotOnWaitlistScreen() {
  const [joining, setJoining] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Auto-join se a usuária chegou aqui logada mas sem entry — caso de callback OAuth.
    let cancelled = false;
    (async () => {
      setJoining(true);
      try {
        const { joinWaitlist } = await import("@/platform/waitlist/api");
        await joinWaitlist("usp");
        if (!cancelled) {
          queryClient.invalidateQueries({ queryKey: ["waitlist", "usp"] });
        }
      } catch (err) {
        console.error("[waitlist] auto-join failed", err);
      } finally {
        if (!cancelled) setJoining(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center" style={uspAccent}>
      <Badge variant="outline">FuvestPro USP</Badge>
      <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold">
        {joining ? "Adicionando você à lista…" : "Você ainda não está na lista."}
      </h1>
      <p className="mt-3 max-w-md text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
        {joining
          ? "Estamos confirmando seu cadastro na lista de espera do FuvestPro USP."
          : "Entre na lista de espera para ser avisada quando o beta abrir."}
      </p>
      {!joining && (
        <Button asChild className="mt-6" size="lg" variant="primary">
          <Link to="/usp">Voltar para a apresentação</Link>
        </Button>
      )}
    </div>
  );
}
